import { memo, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DynamicIcon, ShortcutText } from 'src/components';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { appShortcuts } from 'src/constants/appShortcuts';
import { conversationTypeOptions } from 'src/graphql';
import { useRegisterShortcuts } from 'src/utils/hooks';

type ConversationTypeOption = (typeof conversationTypeOptions)[number];

const TYPE_OPTIONS: SelectMenuOption[] = conversationTypeOptions.map((option) => ({
  value: option.id,
  title: option.label,
  icon: <DynamicIcon weight={500} name={option.icon} />,
}));

const ConversationTypeToggleComponent = ({
  name,
  disabled,
}: {
  name: string;
  disabled?: boolean;
}) => {
  const { control } = useFormContext();
  const triggerRef = useRef<HTMLButtonElement>(null);

  useRegisterShortcuts(
    useMemo(
      () => ({
        ...appShortcuts.changeType,
        callback: () => {
          if (disabled || document.activeElement === triggerRef.current) {
            return false;
          }
          triggerRef.current?.focus();
          triggerRef.current?.click();
          return false;
        },
      }),
      [disabled],
    ),
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: 'Conversation type is required' }}
      render={({ field: { value, onChange } }) => (
        <SelectMenu
          ref={triggerRef}
          label="Type"
          options={TYPE_OPTIONS}
          value={(value as ConversationTypeOption | null)?.id ?? null}
          onChange={(selected) =>
            onChange(
              conversationTypeOptions.find((option) => (option.id as string) === selected),
            )
          }
          search
          hotkey="T"
          variant="inline"
          plainOptionIcons
          disabled={disabled}
          sx={{ width: '100%' }}
          tooltip={
            <ShortcutText
              label={appShortcuts.changeType.description}
              shortcut={appShortcuts.changeType.command}
              size="small"
            />
          }
        />
      )}
    />
  );
};

export const ConversationTypeToggle = memo(ConversationTypeToggleComponent);
